/**
 * Page des sources, de la méthode et des limites.
 *
 * Elle sert autant à la transparence qu'à démontrer que la structure accueille
 * une page de plus sans rien toucher d'autre : un module ici, une entrée dans
 * le routeur, une section dans index.html.
 *
 * Les nombres sont importés des constantes, jamais recopiés : une page de
 * documentation qui contredit le code est pire que pas de documentation.
 *
 * Contenu statique, donc rendu une seule fois.
 */
import {
  ARRIVAL_MAX_HOURS,
  ARRIVAL_MIN_SPEED_KMH,
  FIRE_ANY_DIRECTION_KM,
  FIRE_CLOSE_KM,
  FIRE_REACHING_KM,
  LOCAL_MEASUREMENT_MIN_SENSORS,
  MODEL_MAX_AGE_MIN,
  PLUME_BASE_KM,
  PLUME_SPREAD,
  SENSOR_MAX_AGE_MIN,
  SENSOR_RADIUS_KM,
  SENSOR_TRUST_KM,
  WIND_DIRECTION_UNCERTAINTY_DEG,
  ZONE_LINK_KM,
} from '../../core/constants';
import {
  STATIC_SOURCES_WINDOW,
  STATIC_THERMAL_SOURCES,
} from '../../core/static-sources';
import { WIND_LEVELS } from '../../core/wind';
import { html, render } from '../html';

const SOURCES: readonly (readonly [string, string, string])[] = [
  ['Feux, anomalies thermiques', 'NASA FIRMS et GIBS', 'https://firms.modaps.eosdis.nasa.gov'],
  ['Surfaces déjà brûlées', 'EFFIS, Copernicus', 'https://forest-fire.emergency.copernicus.eu'],
  ["Qualité de l'air modélisée", 'Open-Meteo, modèle CAMS Europe', 'https://open-meteo.com'],
  ['Vent et températures', 'Open-Meteo', 'https://open-meteo.com'],
  ['Capteurs au sol', 'sensor.community', 'https://sensor.community'],
  ['Installations industrielles', 'Géorisques, registre des installations classées', 'https://www.georisques.gouv.fr'],
  ['Adresses', 'Base Adresse Nationale', 'https://adresse.data.gouv.fr'],
  ['Communes', 'geo.api.gouv.fr', 'https://geo.api.gouv.fr'],
  ['Fond de carte', 'OpenStreetMap France', 'https://www.openstreetmap.fr'],
];

/** Nombre de sites industriels distincts dans la liste générée. */
const INDUSTRIAL_SITES = new Set(STATIC_THERMAL_SOURCES.map((source) => source.site)).size;

export function renderSourcesPage(root: HTMLElement): void {
  render(
    root,
    html`
      <div class="prose">
        <h2>Sur quoi repose le verdict</h2>
        <p>
          Quatre sources, par ordre de priorité. Un signalement d'odeur que vous avez
          saisi passe devant tout le reste : votre nez détecte des choses qu'aucun
          capteur situé à vingt kilomètres ne mesure.
        </p>
        <p>
          Viennent ensuite les capteurs citoyens au sol, puis le modèle européen CAMS
          quand aucun capteur n'est disponible. Le vent arrive en dernier, et il ne peut
          que rendre un verdict plus prudent, jamais plus rassurant.
        </p>
        <p>
          Une donnée trop vieille cesse de décider : ${SENSOR_MAX_AGE_MIN} minutes pour
          un capteur, ${MODEL_MAX_AGE_MIN / 60} heures pour le modèle. Passé ce délai,
          l'application affiche "pas d'info" au lieu d'un chiffre périmé.
        </p>
        <p>
          Les seuils portent sur le PM2,5 instantané : moins de 12 propre, moins de 25
          correct, moins de 50 chargé, au-delà très chargé. Ce sont des seuils pour une
          décision immédiate, aérer ou non, et non des moyennes réglementaires sur
          24 heures.
        </p>
        <p>
          L'indice européen EAQI n'est pas affiché, volontairement. C'est une moyenne
          glissante sur 24 heures : pendant un incendie il peut indiquer "extrêmement
          mauvais" alors que l'air est redevenu propre depuis deux heures, et l'inverse
          au début d'un épisode.
        </p>

        <h2>Pourquoi un feu proche empêche de conclure que l'air est bon</h2>
        <p>
          Les capteurs sont cherchés dans un rayon de ${SENSOR_RADIUS_KM} km, parce qu'il
          faut bien en trouver quelque part. Mais un capteur situé à vingt kilomètres ne
          mesure pas l'air de votre salon, et le modèle CAMS, dont la maille fait une
          dizaine de kilomètres, ne voit pas un panache frais.
        </p>
        <p>
          Quand un feu est détecté à proximité, l'application refuse donc de conclure que
          l'air est bon sur la seule foi de ces sources. Elle ne conclut pas l'inverse
          non plus : elle indique le feu, la distance, et le fait qu'aucune mesure assez
          proche ne permet de savoir si la fumée est descendue au sol. La décision reste
          la vôtre, et pour les consignes qui font foi, voyez votre préfecture.
        </p>
        <table>
          <thead>
            <tr>
              <th>Situation</th>
              <th>Le verdict ne peut pas être meilleur que</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Feu pouvant vous atteindre, à ${FIRE_CLOSE_KM} km ou moins</td>
              <td>air chargé</td>
            </tr>
            <tr>
              <td>Feu à ${FIRE_ANY_DIRECTION_KM} km ou moins, quelle que soit la direction</td>
              <td>air chargé</td>
            </tr>
            <tr>
              <td>Vent inexploitable, feu à ${FIRE_CLOSE_KM} km ou moins</td>
              <td>air correct</td>
            </tr>
            <tr>
              <td>Feu pouvant vous atteindre, à ${FIRE_REACHING_KM} km ou moins</td>
              <td>air correct</td>
            </tr>
          </tbody>
        </table>
        <p>
          Une seule chose lève cette réserve : une vraie mesure locale, c'est-à-dire au
          moins ${LOCAL_MEASUREMENT_MIN_SENSORS} capteurs récents à moins de
          ${SENSOR_TRUST_KM} km. Eux constatent l'air que vous respirez, et aucun calcul
          de trajectoire ne peut les contredire.
        </p>

        <h2>Comment le trajet de la fumée est calculé</h2>
        <p>
          Un panache ne s'évase pas en cône large : il s'élargit lentement, d'environ
          10 degrés de demi-angle. L'application mesure donc la distance
          perpendiculaire entre le feu et l'axe du vent, et la compare à la largeur du
          panache à cette distance : le rayon de la zone, plus ${PLUME_BASE_KM} km, plus
          ${PLUME_SPREAD} fois la distance parcourue.
        </p>
        <p>
          Un simple écart d'angle donnerait des fausses alertes. À 100 km, un cône de
          50 degrés couvre plus ou moins 87 km de côté, soit à peu près tout.
        </p>
        <p>
          À cette largeur s'ajoute l'incertitude du modèle météo lui-même, retenue à
          ${WIND_DIRECTION_UNCERTAINTY_DEG} degrés. Un vent annoncé du nord peut souffler
          du nord-nord-est : à 30 km, cela déplace le panache d'une dizaine de
          kilomètres. Les décisions de prudence utilisent donc un couloir élargi, tandis
          que les heures d'arrivée et le tracé de la carte utilisent le panache étroit.
          La carte montre les deux enveloppes.
        </p>

        <h2>À quelle altitude la fumée voyage</h2>
        <p>
          Un panache ne voyage pas à hauteur de haie. Plus un feu est vaste, plus il est
          chaud, plus sa fumée s'élève, et plus le vent qui l'emporte est rapide. Le vent
          mesuré à dix mètres donnait des durées de trajet trois à cinq fois trop
          longues, et désignait les mauvais foyers.
        </p>
        <table>
          <thead>
            <tr>
              <th>Rayon de la zone</th>
              <th>Altitude retenue</th>
            </tr>
          </thead>
          <tbody>
            ${WIND_LEVELS.map(
              (level) => html`
                <tr>
                  <td>
                    ${Number.isFinite(level.maxRadiusKm)
                      ? `moins de ${level.maxRadiusKm} km`
                      : 'au-delà'}
                  </td>
                  <td>${level.label}</td>
                </tr>
              `,
            )}
          </tbody>
        </table>

        <h2>Pourquoi une heure d'arrivée n'est pas toujours donnée</h2>
        <p>
          Si le vent souffle de la même direction depuis des heures, la fumée est déjà
          arrivée, ou déjà passée : annoncer une heure n'aurait aucun sens. Une heure
          d'arrivée ne redevient valable qu'à partir du moment où le vent a tourné, et ce
          moment est cherché séparément pour chaque altitude.
        </p>
        <p>
          Trois situations la rendent muette, et l'application dit alors laquelle. Un vent
          de moins de ${ARRIVAL_MIN_SPEED_KMH} km/h ne permet aucun calcul de durée
          sérieux. Une échéance de plus de ${ARRIVAL_MAX_HOURS} heures serait dépassée par
          le vent lui-même, qui aura tourné entre-temps. Et un feu hors de l'axe n'envoie
          rien.
        </p>
        <p>
          Quand une heure est donnée, elle est arrondie au quart d'heure. Une valeur à la
          minute, issue d'un vent modélisé, promettrait une précision qui n'existe pas.
        </p>

        <h2>Comment les feux sont détectés</h2>
        <p>
          Les satellites VIIRS et MODIS repèrent les anomalies thermiques. L'application
          récupère ces détections, regroupe celles distantes de moins de
          ${ZONE_LINK_KM} km en un même foyer, puis trace le contour de chaque foyer.
        </p>
        <p>
          Ce contour est l'addition des empreintes au sol des pixels qui ont détecté :
          375 mètres de côté pour VIIRS, un kilomètre pour MODIS. Rien n'est ajouté entre
          eux, et un foyer troué est dessiné troué.
        </p>
        <p>
          Le contour était auparavant tracé en reliant les détections les plus extérieures
          par un polygone. Cela remplissait aussi les creux, donc affichait comme brûlé
          ce que le satellite n'avait jamais vu. Sur les données du 28 juillet 2026, huit
          foyers occupaient 1189 km² tracés de cette façon, contre 234 km² réellement
          mesurés. Un foyer de neuf détections près de Poitiers en couvrait 219 à lui
          seul.
        </p>
        <p>
          La grille de lecture est fixe et couvre toute la France, jamais la portion
          affichée à l'écran. Sinon la résolution changerait à chaque zoom et les zones
          se redessineraient différemment à chaque déplacement de la carte.
        </p>

        <h2>Ce que montrent les zones brunes</h2>
        <p>
          Ce sont les surfaces qui ont brûlé au cours des deux dernières semaines, mesurées
          par EFFIS, le service européen de suivi des incendies. Elles sont bien plus
          précises que les zones rouges, parce qu'elles sont dessinées après coup, sur des
          images prises quand la fumée s'est dissipée.
        </p>
        <p>
          Elles ne comptent dans aucun verdict, et c'est voulu : ce qui a brûlé ne brûle
          plus. Des cendres refroidies ne dégagent pas de fumée. Elles servent à situer
          l'ampleur de ce qui s'est passé autour de vous, rien de plus.
        </p>

        <h2>Comment les sites industriels sont écartés</h2>
        <p>
          Les satellites détectent une anomalie thermique, pas un incendie. Les torchères
          de raffinerie, les aciéries, les cimenteries et les incinérateurs brûlent toute
          l'année et apparaissaient comme des feux. Une seule journée d'observation
          donnait Fos-sur-Mer, Dunkerque et Lavéra parmi les foyers signalés.
        </p>
        <p>
          ${STATIC_THERMAL_SOURCES.length} emplacements, répartis sur ${INDUSTRIAL_SITES}
          sites, sont maintenant reconnus comme des sources permanentes. Chacun a franchi
          deux critères indépendants et vérifiables :
        </p>
        <ul>
          <li>
            avoir été détecté au moins ${STATIC_SOURCES_WINDOW.minDaysSeen} jours sur les
            ${STATIC_SOURCES_WINDOW.daysObserved} observés, du
            ${STATIC_SOURCES_WINDOW.from} au ${STATIC_SOURCES_WINDOW.to} ;
          </li>
          <li>
            se trouver à moins de 3 km d'une installation classée industrielle du
            registre officiel Géorisques.
          </li>
        </ul>
        <p>
          Le seuil vient de la mesure. Sur cette période, l'aciérie de Fos-sur-Mer a été
          détectée les ${STATIC_SOURCES_WINDOW.daysObserved} jours, la pétrochimie de
          Lavéra 14 jours, tandis que les incendies alors en cours en Gironde
          plafonnaient à 5 jours. La séparation est nette.
        </p>
        <p>
          Le second critère existe pour ne pas effacer un incendie qui durerait : sans
          installation industrielle à proximité, un emplacement persistant est laissé
          tel quel. Et ces zones ne sont pas supprimées de la carte, seulement affichées
          en gris et sorties du verdict. Si l'une déborde de son empreinte habituelle,
          elle redevient une alerte : une raffinerie qui prend feu produit une fumée bien
          plus toxique qu'une pinède.
        </p>
        <p>
          Le nom affiché est celui de l'installation classée la plus proche, retenue pour
          sa nature d'activité. Ce n'est pas nécessairement elle qui produit la chaleur.
        </p>

        <h2>Ce que l'application ne sait pas faire</h2>
        <ul>
          <li>
            <strong>Ce n'est pas du temps réel.</strong> Les satellites passent quelques
            fois par jour, avec environ trois heures de traitement. Un feu déclaré il y a
            une heure peut ne pas apparaître, et une zone peut rester affichée alors que
            le feu est éteint.
          </li>
          <li>
            <strong>Elle ne donne pas de surface brûlée.</strong> L'étendue affichée est
            celle des taches détectées à la résolution d'affichage du satellite, pas le
            périmètre du feu, et elle surestime souvent un petit foyer. Les périmètres
            réels sont établis par les préfectures et les SDIS, et ne sont pas publiés en
            flux ouvert.
          </li>
          <li>
            <strong>Elle ne distingue pas toujours la nature du feu.</strong> Un brûlage
            agricole, une torchère nouvelle ou une toiture surchauffée produisent la même
            signature qu'un départ de feu.
          </li>
          <li>
            <strong>Les capteurs citoyens ne sont pas homologués.</strong> Ils sont
            nombreux et rapides, mais moins fiables individuellement qu'une station
            officielle. D'où l'usage de la médiane, qu'un capteur déréglé ne déplace pas,
            et non de la moyenne.
          </li>
          <li>
            <strong>Le calcul dépend de la direction du vent.</strong> Quelques degrés
            d'écart changent la liste des feux concernés. C'est une raison de plus de
            faire confiance à la mesure au sol plutôt qu'à la géométrie.
          </li>
        </ul>

        <h2>D'où viennent les données</h2>
        <p>Toutes publiques, toutes sans clé d'API.</p>
        <table>
          <tbody>
            ${SOURCES.map(
              ([label, name, url]) => html`
                <tr>
                  <td>${label}</td>
                  <td><a href="${url}" target="_blank" rel="noopener">${name}</a></td>
                </tr>
              `,
            )}
          </tbody>
        </table>

        <h2>En cas de danger</h2>
        <p>
          Appelez le <strong>112</strong>. Cette application n'est pas officielle et n'est
          pas un dispositif médical. Pour les consignes qui font foi : votre préfecture,
          <a href="https://recosante.beta.gouv.fr" target="_blank" rel="noopener"
            >Recosanté</a
          >
          et
          <a href="https://www.atmo-france.org" target="_blank" rel="noopener">ATMO</a>.
        </p>
      </div>
    `,
  );
}
